import React, { useState, useEffect } from 'react';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const PreferencesSection = () => {
  const [preferences, setPreferences] = useState({
    language: 'en',
    timezone: 'America/New_York',
    dateFormat: 'MM/DD/YYYY',
    timeFormat: '12',
    theme: 'light',
    dashboardRefresh: '30',
    incidentView: 'table',
    pageSize: '25'
  });

  const [displaySettings, setDisplaySettings] = useState({
    showAvatars: true,
    showTimestamps: true,
    showStatusIcons: true,
    compactMode: false,
    showSidebar: true,
    autoExpandDetails: false
  });

  const [accessibilitySettings, setAccessibilitySettings] = useState({
    highContrast: false,
    reducedMotion: false,
    screenReader: false,
    keyboardNavigation: true,
    fontSize: 'medium'
  });

  const languageOptions = [
    { value: 'en', label: 'English (US)' },
    { value: 'en-gb', label: 'English (UK)' },
    { value: 'es', label: 'Español' },
    { value: 'fr', label: 'Français' },
    { value: 'de', label: 'Deutsch' }
  ];

  const timezoneOptions = [
    { value: 'America/New_York', label: 'Eastern Time (ET)' },
    { value: 'America/Chicago', label: 'Central Time (CT)' },
    { value: 'America/Denver', label: 'Mountain Time (MT)' },
    { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
    { value: 'UTC', label: 'Coordinated Universal Time (UTC)' },
    { value: 'Europe/London', label: 'Greenwich Mean Time (GMT)' },
    { value: 'Europe/Berlin', label: 'Central European Time (CET)' },
    { value: 'Asia/Tokyo', label: 'Japan Standard Time (JST)' }
  ];

  const dateFormatOptions = [
    { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY (US)' },
    { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY (UK)' },
    { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD (ISO)' },
    { value: 'DD MMM YYYY', label: 'DD MMM YYYY' }
  ];

  const timeFormatOptions = [
    { value: '12', label: '12-hour (AM/PM)' },
    { value: '24', label: '24-hour' }
  ];

  const themeOptions = [
    { value: 'light', label: 'Light Theme' },
    { value: 'dark', label: 'Dark Theme' },
    { value: 'auto', label: 'Auto (System)' }
  ];

  const refreshOptions = [
    { value: '15', label: '15 seconds' },
    { value: '30', label: '30 seconds' },
    { value: '60', label: '1 minute' },
    { value: '300', label: '5 minutes' },
    { value: '0', label: 'Manual only' }
  ];

  const viewOptions = [
    { value: 'table', label: 'Table View' },
    { value: 'card', label: 'Card View' },
    { value: 'list', label: 'List View' }
  ];

  const pageSizeOptions = [
    { value: '10', label: '10 per page' },
    { value: '25', label: '25 per page' },
    { value: '50', label: '50 per page' },
    { value: '100', label: '100 per page' }
  ];

  const fontSizeOptions = [
    { value: 'small', label: 'Small' },
    { value: 'medium', label: 'Medium' },
    { value: 'large', label: 'Large' },
    { value: 'extra-large', label: 'Extra Large' }
  ];

  // Load preferences from localStorage on component mount
  useEffect(() => {
    const savedLanguage = localStorage.getItem('currentLanguage');
    if (savedLanguage) {
      setPreferences(prev => ({ ...prev, language: savedLanguage }));
    }
  }, []);

  const handlePreferenceChange = (key, value) => {
    setPreferences(prev => ({
      ...prev,
      [key]: value
    }));

    // Save language preference to localStorage
    if (key === 'language') {
      localStorage.setItem('currentLanguage', value);
    }
  };

  const handleDisplayChange = (key, checked) => {
    setDisplaySettings(prev => ({
      ...prev,
      [key]: checked
    }));
  };

  const handleAccessibilityChange = (key, value) => {
    setAccessibilitySettings(prev => ({
      ...prev,
      [key]: typeof value === 'boolean' ? value : value
    }));
  };

  const handleSavePreferences = () => {
    console.log('Saving preferences:', {
      preferences,
      displaySettings,
      accessibilitySettings
    });
  };

  const handleResetToDefaults = () => {
    setPreferences({
      language: 'en',
      timezone: 'America/New_York',
      dateFormat: 'MM/DD/YYYY',
      timeFormat: '12',
      theme: 'light',
      dashboardRefresh: '30',
      incidentView: 'table',
      pageSize: '25'
    });
    setDisplaySettings({
      showAvatars: true,
      showTimestamps: true,
      showStatusIcons: true,
      compactMode: false,
      showSidebar: true,
      autoExpandDetails: false
    });
    setAccessibilitySettings({
      highContrast: false,
      reducedMotion: false,
      screenReader: false,
      keyboardNavigation: true,
      fontSize: 'medium'
    });
  };

  const getCurrentTime = () => {
    const now = new Date();
    const timeZone = preferences?.timezone;
    const format = preferences?.timeFormat === '12' ? 'en-US' : 'en-GB';
    
    return now?.toLocaleString(format, {
      timeZone,
      hour12: preferences?.timeFormat === '12',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      <div className="flex items-center space-x-3 mb-6">
        <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center">
          <Icon name="Settings" size={24} color="white" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-foreground">Preferences</h3>
          <p className="text-sm text-muted-foreground">Customize your application experience</p>
        </div>
      </div>
      <div className="space-y-8">
        {/* Language & Region */}
        <div className="space-y-4">
          <h4 className="font-medium text-foreground flex items-center space-x-2">
            <Icon name="Globe" size={18} />
            <span>Language & Region</span>
          </h4>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select
              label="Language"
              description="Choose your preferred language"
              options={languageOptions}
              value={preferences?.language}
              onChange={(value) => handlePreferenceChange('language', value)}
            />

            <Select
              label="Timezone"
              description={`Current time: ${getCurrentTime()}`}
              options={timezoneOptions}
              value={preferences?.timezone}
              onChange={(value) => handlePreferenceChange('timezone', value)}
            />

            <Select
              label="Date Format"
              description="How dates are displayed"
              options={dateFormatOptions}
              value={preferences?.dateFormat}
              onChange={(value) => handlePreferenceChange('dateFormat', value)}
            />

            <Select
              label="Time Format"
              description="12-hour or 24-hour time"
              options={timeFormatOptions}
              value={preferences?.timeFormat}
              onChange={(value) => handlePreferenceChange('timeFormat', value)}
            />
          </div>
        </div>

        {/* Appearance */}
        <div className="space-y-4">
          <h4 className="font-medium text-foreground flex items-center space-x-2">
            <Icon name="Palette" size={18} />
            <span>Appearance</span>
          </h4>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select
              label="Theme"
              description="Choose your preferred theme"
              options={themeOptions}
              value={preferences?.theme}
              onChange={(value) => handlePreferenceChange('theme', value)}
            />

            <Select
              label="Dashboard Refresh"
              description="How often to refresh data"
              options={refreshOptions}
              value={preferences?.dashboardRefresh}
              onChange={(value) => handlePreferenceChange('dashboardRefresh', value)}
            />

            <Select
              label="Incident View"
              description="Default view for incident lists"
              options={viewOptions}
              value={preferences?.incidentView}
              onChange={(value) => handlePreferenceChange('incidentView', value)}
            />

            <Select
              label="Items Per Page"
              description="Number of items to show per page"
              options={pageSizeOptions}
              value={preferences?.pageSize}
              onChange={(value) => handlePreferenceChange('pageSize', value)}
            />
          </div>
        </div>

        {/* Display Options */}
        <div className="space-y-4">
          <h4 className="font-medium text-foreground flex items-center space-x-2">
            <Icon name="Eye" size={18} />
            <span>Display Options</span>
          </h4>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Checkbox
              label="Show User Avatars"
              description="Display profile pictures in lists"
              checked={displaySettings?.showAvatars}
              onChange={(e) => handleDisplayChange('showAvatars', e?.target?.checked)}
            />

            <Checkbox
              label="Show Timestamps"
              description="Display creation and update times"
              checked={displaySettings?.showTimestamps}
              onChange={(e) => handleDisplayChange('showTimestamps', e?.target?.checked)}
            />

            <Checkbox
              label="Show Status Icons"
              description="Display visual status indicators"
              checked={displaySettings?.showStatusIcons}
              onChange={(e) => handleDisplayChange('showStatusIcons', e?.target?.checked)}
            />

            <Checkbox
              label="Compact Mode"
              description="Reduce spacing for more content"
              checked={displaySettings?.compactMode}
              onChange={(e) => handleDisplayChange('compactMode', e?.target?.checked)}
            />

            <Checkbox
              label="Show Sidebar"
              description="Keep navigation sidebar visible"
              checked={displaySettings?.showSidebar}
              onChange={(e) => handleDisplayChange('showSidebar', e?.target?.checked)}
            />

            <Checkbox
              label="Auto-expand Details"
              description="Automatically expand incident details"
              checked={displaySettings?.autoExpandDetails}
              onChange={(e) => handleDisplayChange('autoExpandDetails', e?.target?.checked)}
            />
          </div>
        </div>

        {/* Accessibility */}
        <div className="space-y-4">
          <h4 className="font-medium text-foreground flex items-center space-x-2">
            <Icon name="Accessibility" size={18} />
            <span>Accessibility</span>
          </h4>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Checkbox
              label="High Contrast Mode"
              description="Increase contrast for better visibility"
              checked={accessibilitySettings?.highContrast}
              onChange={(e) => handleAccessibilityChange('highContrast', e?.target?.checked)}
            />

            <Checkbox
              label="Reduced Motion"
              description="Minimize animations and transitions"
              checked={accessibilitySettings?.reducedMotion}
              onChange={(e) => handleAccessibilityChange('reducedMotion', e?.target?.checked)}
            />

            <Checkbox
              label="Screen Reader Support"
              description="Optimize for screen reading software"
              checked={accessibilitySettings?.screenReader}
              onChange={(e) => handleAccessibilityChange('screenReader', e?.target?.checked)}
            />

            <Checkbox
              label="Keyboard Navigation"
              description="Enable keyboard shortcuts and navigation"
              checked={accessibilitySettings?.keyboardNavigation}
              onChange={(e) => handleAccessibilityChange('keyboardNavigation', e?.target?.checked)}
            />

            <div className="md:col-span-2">
              <Select
                label="Font Size"
                description="Adjust text size for better readability"
                options={fontSizeOptions}
                value={accessibilitySettings?.fontSize}
                onChange={(value) => handleAccessibilityChange('fontSize', value)}
                className="max-w-xs"
              />
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center justify-between pt-6 border-t border-border">
          <Button
            variant="outline"
            iconName="RotateCcw"
            iconPosition="left"
            onClick={handleResetToDefaults}
          >
            Reset to Defaults
          </Button>
          
          <Button
            variant="default"
            iconName="Save"
            iconPosition="left"
            onClick={handleSavePreferences}
          >
            Save Preferences
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PreferencesSection;